// apps/merchant-v3/src/hooks/useBillingSummary.ts
//
// Phase 6 Wave 1 Task 02 — Originally landed as a stub returning `null`.
//
// Phase 6 Wave 10 Task 32 — REAL implementation. Wires the
// `GET /api/v1/merchants/me/billing-summary` endpoint behind TanStack
// Query (30s polling) + WebSocket live-refresh on the
// `merchant.${id}.billing` channel (INVOICE_CREATED / INVOICE_PAID /
// INVOICE_OVERDUE → invalidate the summary + invoices list caches).
//
// Used by:
//   • Sidebar Invoices badge — red when overdue_count > 0
//     yellow when due_soon_count > 0.
//   • TopBar OutstandingChip — INVOICE-mode merchants see
//     the outstanding total in place of the wallet balance chip.
//   • BillingOverviewPage / InvoicesListPage — KPI cards + page header.
//
// Backwards compatibility: the original Task 02 stub typed
// `BillingSummary` with `outstandingTotalCents / outstandingCurrency /
// overdueCount / dueSoonCount / nextDueAt`. Existing consumers
// (`InvoicesBadge`, `OutstandingChip`) read those camelCase fields. We
// keep that interface AND add the new BE-shaped fields
// (`wallet_balance / outstanding / overdue_count / due_soon_count /
// billing_mode`) on the same object so both surfaces compile against
// one unified hook return type — no consumer code-mod needed in this
// task. The `useBillingSummaryQuery()` companion exposes the raw
// TanStack Query result for callers that need loading / error state.
//
// vg-binding: PLAN:task-02
// vg-binding: PLAN:task-32
// vg-binding: P6.D-245
// vg-binding: P6.D-246
// vg-binding: P6.D-247
// vg-binding: G-52
// vg-binding: G-53

import { useEffect } from 'react';
import {
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';

import { ApiError } from '@/lib/api/client';
import { useAuth } from '@/lib/auth/AuthProvider';
import { getBillingSummary } from '@/services/billingClient';
import { subscribe, type WSMessage } from '@/services/wsClient';
import type {
  BillingMode,
  BillingSummaryV2,
  BillingWsMessage,
} from '@/types/billing';

// --- query keys ----------------------------------------------------------

export const BILLING_QUERY_KEYS = {
  summary: ['billing', 'summary'] as const,
  invoices: (filters: Record<string, unknown>) =>
    ['billing', 'invoices', filters] as const,
};

const POLL_MS = 30_000; // 30s polling cadence
const STALE_MS = 15_000; // half the poll cadence keeps cache warm

// --- public types --------------------------------------------------------

/**
 * Unified BillingSummary shape combining the legacy Task 02 stub fields
 * (camelCase) with the new BE contract fields (snake_case). New code
 * SHOULD prefer the snake_case fields; the camelCase aliases remain so
 * the Wave 1 consumers (InvoicesBadge, OutstandingChip) keep compiling.
 */
export interface BillingSummary extends BillingSummaryV2 {
  // ---- legacy aliases (Task 02) ----
  /** Outstanding total expressed in MINOR currency units (cents) for
   *  UI helpers that already format with `cents/100`. Derived from
   *  `outstanding * 100` to keep the BE source-of-truth in major units. */
  outstandingTotalCents: number;
  /** Currency code copy of `summary.currency` (defaults to 'USD'). */
  outstandingCurrency: string;
  /** Number of overdue invoices — alias of `overdue_count`. */
  overdueCount: number;
  /** Number of due-soon invoices — alias of `due_soon_count`. */
  dueSoonCount: number;
  /** ISO-8601 timestamp of the soonest due invoice when known.
   *  The Task 02 stub typed this as required-but-nullable; the new BE
   *  contract does not surface it directly so we leave it `null` and
   *  let downstream tasks (33/34) populate when they add detail data. */
  nextDueAt: string | null;
}

/** Re-export the BE shape so callers that only need the new contract
 *  fields can import it without pulling the legacy alias surface. */
export type { BillingSummaryV2, BillingMode };

// --- helpers -------------------------------------------------------------

function projectSummary(raw: BillingSummaryV2): BillingSummary {
  const currency = raw.currency ?? 'USD';
  return {
    ...raw,
    currency,
    outstandingTotalCents: Math.round(raw.outstanding * 100),
    outstandingCurrency: currency,
    overdueCount: raw.overdue_count,
    dueSoonCount: raw.due_soon_count,
    nextDueAt: null,
  };
}

// --- live-refresh hook ---------------------------------------------------

/**
 * Subscribe to the merchant's billing channel for the lifetime of the
 * caller component. Invalidates the summary + invoices caches on every
 * INVOICE_CREATED / INVOICE_PAID / INVOICE_OVERDUE push (
 * realtime sidebar badge). No-op when `merchantId` is falsy.
 */
export function useBillingLiveUpdates(
  merchantId: string | null | undefined,
): void {
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!merchantId) return;
    const channel = `merchant.${merchantId}.billing`;
    const unsubscribe = subscribe(channel, (msg: WSMessage) => {
      // The wsClient union covers Order + Wallet messages today; the
      // billing variants land via the same generic WS frame so we cast
      // narrowly here. (Future task can promote BillingWsMessage into
      // the WSMessage union once the BE channel is live.)
      const evt = msg as unknown as BillingWsMessage;
      if (
        evt &&
        typeof evt === 'object' &&
        (evt.type === 'INVOICE_CREATED' ||
          evt.type === 'INVOICE_PAID' ||
          evt.type === 'INVOICE_OVERDUE')
      ) {
        void queryClient.invalidateQueries({
          queryKey: BILLING_QUERY_KEYS.summary,
        });
        void queryClient.invalidateQueries({ queryKey: ['billing', 'invoices'] });
      }
    });
    return () => {
      unsubscribe();
    };
  }, [merchantId, queryClient]);
}

// --- query hook ----------------------------------------------------------

/**
 * Raw TanStack Query handle — exposes `isLoading / error / refetch` for
 * pages that need to render loading skeletons or error states. Polling
 * cadence is 30s; the WS push invalidates immediately so
 * the poll is just a pessimistic fallback.
 */
export function useBillingSummaryQuery(): UseQueryResult<
  BillingSummary,
  ApiError
> {
  const { user } = useAuth();
  useBillingLiveUpdates(user?.id);
  return useQuery<BillingSummary, ApiError>({
    queryKey: BILLING_QUERY_KEYS.summary,
    queryFn: async () => projectSummary(await getBillingSummary()),
    staleTime: STALE_MS,
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
  });
}

/**
 * Convenience selector — returns the BillingSummary value (or `null`
 * while the first fetch is in flight). Backwards-compatible signature
 * so the Wave 1 InvoicesBadge / OutstandingChip consumers keep working
 * without any code change.
 */
export function useBillingSummary(): BillingSummary | null {
  const q = useBillingSummaryQuery();
  return q.data ?? null;
}
